import { odometerText } from '../../../utils/units';
import React from 'react';
import styled from 'styled-components';
import { URLS } from '../../../../constants/urls';
import { IGetCarsByDealerQuery } from '../../../services/carService/__generated__/queries.generated';
import { Link } from 'react-router-dom';
import tw from 'twin.macro';
import { CarStatus } from '../../../../__generated__/graphql';

// Styled components matching the landing page style
const Card = styled.div`
  ${tw`
    bg-black
    rounded-lg
    overflow-hidden
    shadow-card-hover
    transition-transform
    duration-300
    box-border
  `}

  &:hover {
    ${tw`transform scale-105`}
  }
`;

const ImageContainer = styled.div`
  ${tw`
    relative
    h-48
    overflow-hidden
    box-border
  `}
`;

const CarImage = styled.img`
  ${tw`
    w-full
    h-full
    object-cover
  `}
`;

const CardContent = styled.div`
  ${tw`
    p-4
    bg-black
    box-border
  `}
`;

const CarTitle = styled.h2`
  ${tw`
    text-lg
    font-bold
    mb-2
    text-white
  `}
`;

const Price = styled.span`
  ${tw`
    text-xl
    font-bold
    text-white
    mb-2
    block
  `}
`;

const CarDetails = styled.p`
  ${tw`
    text-text-tertiary
    mb-4
  `}
`;

const ViewButton = styled(Link)`
  ${tw`
    inline-block
    bg-white
    text-text-primary
    py-2
    px-4
    rounded-md
    font-medium
    transition-colors
    duration-200
    w-full
    text-center
    box-border
  `}

  &:hover {
    ${tw`bg-bg-secondary`}
  }
`;

const StatusBadge = styled.span<{ status: CarStatus }>`
  ${tw`
    absolute
    top-2
    right-2
    px-2
    py-1
    rounded-full
    text-xs
    font-semibold
    text-white
  `}
  ${({ status }) => {
    switch (status) {
      case CarStatus.Available:
        return tw`bg-green-500`;
      case CarStatus.Deactivated:
        return tw`bg-gray-500`;
      case CarStatus.Pending:
        return tw`bg-yellow-500`;
      case CarStatus.Selling:
        return tw`bg-black`;
      case CarStatus.Sold:
        return tw`bg-red-500`;
      default:
        return tw`bg-gray-500`;
    }
  }}
`;

// Format price helper function
const formatPrice = (price: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(price);
};

// Component interface
type CarCardProps = IGetCarsByDealerQuery['getAllCarsOnSaleByDealer'][0] & {
  onDetailClicked?: () => void;
  onCardClicked?: () => void;
  status?: CarStatus;
};

const CarCard: React.FC<CarCardProps> = ({
  id,
  name,
  year,
  make,
  model,
  mileage,
  odometerUnit,
  listedSalePrice,
  thumbnailUrl,
  gas,
  seats,
  status,
  onCardClicked,
}) => {
  // Create details string
  const detailsArray = [];
  if (gas) detailsArray.push(gas);
  if (seats) detailsArray.push(`${seats} seats`);
  if (mileage) detailsArray.push(odometerText(mileage, odometerUnit));

  const details = detailsArray.join(', ');

  return (
    <Card onClick={onCardClicked}>
      <ImageContainer>
        <CarImage
          src={
            thumbnailUrl
              ? `${URLS.S3_BUCKET.CAR_THUMBNAILS}/${thumbnailUrl}`
              : `${URLS.S3_BUCKET.CAR_THUMBNAILS}/car-images/default-car.jpg`
          }
          alt={name || 'Car image'}
        />
        {status && <StatusBadge status={status as CarStatus}>{status}</StatusBadge>}
      </ImageContainer>

      <CardContent>
        <CarTitle>{name}</CarTitle>
        <Price>{formatPrice(listedSalePrice ?? 0)}</Price>
        <CarDetails>{details}</CarDetails>
        <ViewButton to={`/car-view/${id}`}>View Details</ViewButton>
      </CardContent>
    </Card>
  );
};

export default CarCard;
